import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CreditCard, Landmark } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayInvoice() {
  const [params] = useSearchParams();
  const invId = params.get("inv") || "";
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);

  const load = () =>
    axios
      .get(`${API}/invoices/public/${invId}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Invoice not found. Please check the link or call us on 03 9113 6005."));

  useEffect(() => {
    if (!invId) { setErr("No invoice specified."); return; }
    if (params.get("cancelled")) toast.info("Payment cancelled — you can try again below.");
    if (sessionId) {
      let tries = 0;
      const poll = async () => {
        try {
          const { data: st } = await axios.get(`${API}/invoices/pay/status/${sessionId}`);
          if (st.status === "collected") { load(); return; }
        } catch {}
        if (++tries < 6) setTimeout(poll, 2000);
        else load();
      };
      poll();
    } else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invId]);

  const payByCard = async () => {
    setPaying(true);
    try {
      const { data: res } = await axios.post(`${API}/invoices/${invId}/pay/card`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not start card payment.");
      setPaying(false);
    }
  };

  if (err)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-5 text-center text-sm text-slate-600" data-testid="pay-invoice-error">
        {err}
      </div>
    );
  if (!data)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-blaze" />
      </div>
    );

  const paid = data.status === "paid";

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10" data-testid="pay-invoice-page">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 flex justify-center"><Logo /></div>
        <div className="overflow-hidden rounded-3xl border border-line bg-white shadow-xl">
          <div className="bg-slate-900 p-6 text-center text-white">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-blue-300">Invoice</p>
            <h1 className="mt-1 font-display text-2xl font-bold">{data.invoice_number}</h1>
            <p className="mt-1 text-xs text-slate-300">{data.customer_name}</p>
          </div>
          <div className="p-6">
            <div className="divide-y divide-line/60 text-sm" data-testid="pay-invoice-items">
              {(data.line_items || []).map((it, i) => (
                <div key={i} className="flex items-start justify-between gap-3 py-2.5">
                  <div>
                    <p className="font-semibold text-slate-900">{it.description}</p>
                    <p className="text-[11px] text-slate-400">
                      {[it.job_id, it.date, it.rate ? `${it.hours} hrs × $${it.rate}/hr` : null].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <span className="whitespace-nowrap font-semibold text-slate-900">${Number(it.amount).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-2.5 text-slate-600">
                <span>Subtotal</span>
                <span className="font-semibold text-slate-900">${Number(data.subtotal).toFixed(2)}</span>
              </div>
              {data.paid_total > 0 && (
                <div className="flex items-center justify-between py-2.5 text-slate-600">
                  <span>Paid</span>
                  <span className="font-semibold text-green-700">-${Number(data.paid_total).toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-3">
                <span className="font-display text-base font-bold uppercase text-slate-900">Amount Due</span>
                <span className="font-display text-2xl font-bold text-blaze" data-testid="pay-invoice-total">
                  ${Number(data.outstanding).toFixed(2)}
                </span>
              </div>
            </div>

            {paid ? (
              <div className="mt-4 rounded-2xl border border-green-300 bg-green-50 p-5 text-center" data-testid="pay-invoice-paid">
                <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
                <p className="mt-2 font-display text-lg font-bold uppercase text-green-700">Paid — Thank You!</p>
                <p className="mt-1 text-xs text-slate-600">This invoice has been paid in full.</p>
              </div>
            ) : (
              <>
                <button
                  onClick={payByCard}
                  disabled={paying}
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-blaze py-4 text-sm font-bold uppercase tracking-[0.15em] text-white active:scale-[0.99] disabled:opacity-60"
                  data-testid="pay-invoice-card-btn"
                >
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  Pay ${Number(data.card_total).toFixed(2)} by Card
                </button>
                <p className="mt-1.5 text-center text-[11px] text-slate-400">
                  Includes {data.surcharge_pct}% card surcharge (${Number(data.surcharge || 0).toFixed(2)}).
                </p>
                {data.bank?.bsb && (
                  <div className="mt-4 rounded-2xl border border-line bg-slate-50 p-4" data-testid="pay-invoice-bank">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-700">
                      <Landmark className="h-4 w-4 text-blaze" /> Or pay by bank transfer
                    </p>
                    <div className="mt-2 space-y-1 text-sm text-slate-700">
                      {data.bank.account_name && <p><span className="text-slate-400">Account:</span> {data.bank.account_name}</p>}
                      <p><span className="text-slate-400">BSB:</span> {data.bank.bsb} · <span className="text-slate-400">Acc No:</span> {data.bank.account_number}</p>
                      <p><span className="text-slate-400">Reference:</span> <strong>{data.invoice_number}</strong></p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">
          Questions? Call us on <a href="tel:+61391136005" className="font-semibold text-blaze">03 9113 6005</a>
        </p>
      </div>
    </div>
  );
}
